import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  CheckBadgeIcon,
  DocumentMagnifyingGlassIcon,
  ClipboardDocumentIcon,
  ChartPieIcon,
  BellIcon,
  EyeIcon,
  PencilSquareIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { WebPageSchema, BreadcrumbSchema } from '../../components/SEO/StructuredData';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DentalComplianceSoftware() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const cqcRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'Dental Consent Software | UK practices | Smile Consent',
    'AI-assisted consent drafting, digital signing, and records to support your practice’s own consent and audit processes. Clinician-reviewed before use — not a substitute for professional judgment.',
    'https://smileconsent.co.uk/dental-compliance-software'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const cqcStandards = [
    {
      standard: 'Safe',
      description: 'People are protected from abuse and avoidable harm',
      requirements: [
        'Risk assessments and management',
        'Incident reporting and investigation',
        'Safeguarding procedures',
        'Infection prevention and control',
        'Medicines management'
      ],
    },
    {
      standard: 'Effective',
      description: 'People\'s care, treatment and support achieves good outcomes',
      requirements: [
        'Evidence-based practice',
        'Clinical governance',
        'Quality improvement',
        'Staff training and development',
        'Patient outcomes monitoring'
      ],
    },
    {
      standard: 'Caring',
      description: 'Staff involve and treat people with compassion, kindness, dignity and respect',
      requirements: [
        'Patient communication',
        'Consent procedures',
        'Privacy and dignity',
        'Cultural sensitivity',
        'Complaints handling'
      ],
    },
    {
      standard: 'Responsive',
      description: 'Services are organised so that they meet people\'s needs',
      requirements: [
        'Accessible services',
        'Reasonable adjustments',
        'Service planning',
        'Patient feedback',
        'Continuity of care'
      ],
    },
    {
      standard: 'Well-led',
      description: 'Leadership, management and governance assures the delivery of high-quality care',
      requirements: [
        'Leadership and management',
        'Governance systems',
        'Staff engagement',
        'Continuous improvement',
        'Partnership working'
      ],
    }
  ];

  const complianceFeatures = [
    {
      icon: DocumentMagnifyingGlassIcon,
      title: 'Inspection preparation support',
      description: 'Tools to help you gather consent records and documentation your practice may need when preparing for scrutiny — you remain responsible for CQC compliance.',
      benefits: [
        'Consent and activity records in one place',
        'Clearer evidence of what was shared with patients',
        'Less time hunting for PDFs before visits',
        'Export and reporting where available'
      ]
    },
    {
      icon: ClipboardDocumentIcon,
      title: 'Activity and consent records',
      description: 'Timestamps and logs for consent-related actions where the product provides them, to support your own record-keeping.',
      benefits: [
        'Send and access events where enabled',
        'Structured storage of signed materials where configured',
        'Backups depend on your plan and infrastructure',
        'Search and retrieval from the app where available'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Governance mindset',
      description: 'Clearer documentation makes team reviews easier — the practice still owns risk management and clinical governance.',
      benefits: [
        'Editable drafts before anything goes to the patient',
        'Separation of draft vs sent where the workflow supports it',
        'Less reliance on scattered paper or ad-hoc files',
        'Supports your existing policies when used consistently'
      ]
    },
    {
      icon: ChartPieIcon,
      title: 'Usage visibility',
      description: 'See how consent activity is progressing in your account (where analytics are available on your plan).',
      benefits: [
        'High-level activity where offered',
        'Plan limits and usage indicators',
        'Fewer unknowns about pending consents',
        'Operational reporting where built'
      ]
    },
    {
      icon: BellIcon,
      title: 'Reminders',
      description: 'Optional reminders for consent workflows where the product includes them — not a substitute for practice-wide task systems.',
      benefits: [
        'Patient-facing links and expiry where configured',
        'Email reminders where enabled',
        'Fewer forgotten follow-ups for consents in flight',
        'Configurable options where available'
      ]
    },
    {
      icon: DocumentTextIcon,
      title: 'Document Management',
      description: 'Centralized document storage with version control and approval workflows.',
      benefits: [
        'Secure document storage',
        'Version control',
        'Approval workflows',
        'Consent document storage'
      ]
    }
  ];

  const complianceStats = [
    {
      metric: 'Records',
      description: 'Digital consent trails to support your own governance reviews',
      icon: '📋'
    },
    {
      metric: 'Clarity',
      description: 'Patient-facing wording you can edit before it is sent',
      icon: '✏️'
    },
    {
      metric: 'Control',
      description: 'Your practice decides what is sent and what is stored',
      icon: '🔒'
    }
  ];

  const inspectionChecklist = [
    'All consent forms are properly completed and stored',
    'Staff training records are up to date',
    'Risk assessments are current and comprehensive',
    'Incident reports are properly documented',
    'Patient feedback is collected and acted upon',
    'Clinical governance procedures are followed',
    'Infection prevention protocols are in place',
    'Medicines management processes are documented',
    'Safeguarding procedures are current',
    'Quality improvement plans are active'
  ];

  return (
    <>
      <Helmet>
        <title>Dental Consent Software for UK Practices | Smile Consent</title>
        <meta name="description" content="Digital consent and AI-assisted drafting for UK dental practices, with signing and records to support your GDPR and professional documentation processes." />
        <meta name="keywords" content="dental consent software, digital consent UK, dental audit trail, GDPR dental software, UK dental practice" />
        <link rel="canonical" href="https://smileconsent.co.uk/dental-compliance-software" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Dental Consent Software for UK Practices | Smile Consent" />
        <meta property="og:description" content="AI-assisted consent drafting and digital signing. Your practice remains responsible for regulatory compliance and clinical decisions." />
        <meta property="og:url" content="https://smileconsent.co.uk/dental-compliance-software" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dental Consent Software for UK Practices | Smile Consent" />
        <meta name="twitter:description" content="AI-assisted consent drafting and digital signing for UK dental teams — clinician-reviewed before use." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(WebPageSchema({
            name: "Dental Compliance Software",
            url: "https://smileconsent.co.uk/dental-compliance-software",
            description: "Digital consent and AI-assisted drafting for UK dental practices. Professional review required; not a substitute for legal or regulatory advice."
          }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(BreadcrumbSchema({
            items: [{ label: 'Dental Compliance Software', path: '/dental-compliance-software' }]
          }))}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Breadcrumb items={[
            { label: 'Dental Compliance Software', path: '/dental-compliance-software' }
          ]} />
        </div>

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-purple-100 via-blue-100 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Dental Consent Compliance Software
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              AI-assisted consent drafting, digital signing, and records to support your practice&apos;s documentation.
              Your team reviews and approves everything before it reaches patients — one platform for consent workflows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <ShieldCheckIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CQC Compliance Banner */}
      <section className="bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckBadgeIcon className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Stronger consent documentation</h2>
            </div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              SmileConsent helps you document consent clearly and retrieve records when you need them.
              CQC outcomes depend on your whole practice — we do not guarantee ratings or inspection results.
            </p>
          </div>
        </div>
      </section>

      {/* CQC Standards Section */}
      <section ref={cqcRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How consent fits the wider picture
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The CQC key lines of enquiry describe what good care looks like. Good consent documentation supports several of these themes — it does not replace your wider quality systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cqcStandards.map((standard, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{standard.standard}</h3>
                </div>
                <p className="text-gray-600 mb-4">{standard.description}</p>
                <div className="space-y-2">
                  {standard.requirements.map((requirement, reqIndex) => (
                    <div key={reqIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Features Section */}
      <section ref={featuresRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Features at a glance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tools to support consent documentation and retrieval — your practice remains responsible for regulatory compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {complianceFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inspection Checklist Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Good documentation habits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Examples many practices review when preparing for scrutiny — not exhaustive and not legal advice. Adapt to your own policies and professional guidance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl animate-on-scroll">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Pre-Inspection Checklist</h3>
              <div className="space-y-4">
                {inspectionChecklist.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="bg-blue-100 p-1 rounded">
                      <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-600 text-white p-8 rounded-xl animate-on-scroll">
              <h3 className="text-2xl font-bold mb-6">What SmileConsent can help with</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>Central list of consent requests and statuses</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>Retrieve sent consent materials from the app</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>Activity logs where the product records them</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>Export or reporting where your plan includes it</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>Consistent digital signing flow for patients</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span>AI-assisted drafting with mandatory clinician review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Stats Section */}
      <section ref={testimonialsRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for real practice workflows
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Clearer consent records and less administrative friction — outcomes vary by how each practice uses the platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {complianceStats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg animate-on-scroll text-center">
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.metric}</div>
                <p className="text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to streamline consent?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Start a free trial and see how AI-assisted drafting and digital signing can fit your team — with clinicians always in control of the final wording.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-plans"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <ChartBarIcon className="h-6 w-6" />
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CPD Promotional Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-full inline-flex items-center gap-2 px-4 py-2 mb-6">
            <AcademicCapIcon className="h-5 w-5 text-white" />
            <span className="text-white text-sm font-semibold">FREE CPD COURSE</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Legal Consent in Dentistry
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Free 1-hour verifiable CPD covering GDC standards, CQC consent themes, and documentation good practice
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white text-sm"><strong>GDC Outcomes:</strong> A, B, C, D</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white text-sm"><strong>Duration:</strong> 1 Hour</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <p className="text-white text-sm"><strong>Certificate:</strong> Included</p>
            </div>
          </div>
          <Link
            to="/cpd-legal-consent"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-bold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <AcademicCapIcon className="h-6 w-6" />
            Start Free CPD Course
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                Digital consent for UK dental practices. Unlimited users on plans where offered — see pricing.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}